import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { showToast } from '../utils/toast';
import logo from '../assets/logo.jpg';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { loginAdmin, loginClient, loginModerator } = useAuth();

  // Admin form state
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  // Client form state
  const [clientUsername, setClientUsername] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [clientLoading, setClientLoading] = useState(false);

  // Moderator form state
  const [moderatorUsername, setModeratorUsername] = useState('');
  const [moderatorPassword, setModeratorPassword] = useState('');
  const [moderatorLoading, setModeratorLoading] = useState(false);

  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdminLoading(true);

    try {
      await loginAdmin({ username: adminUsername, password: adminPassword });
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/');
    } catch (err: any) {
      showToast.error(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleClientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setClientLoading(true);

    try {
      await loginClient({ username: clientUsername, password: clientPassword });
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/');
    } catch (err: any) {
      showToast.error(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setClientLoading(false);
    }
  };

  const handleModeratorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModeratorLoading(true);

    try {
      await loginModerator({ username: moderatorUsername, password: moderatorPassword });
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/');
    } catch (err: any) {
      showToast.error(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setModeratorLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <img 
                src={logo} 
                alt="Hilton Auto Spare Parts" 
                className="h-24 w-24 rounded-full shadow-xl ring-4 ring-indigo-100" 
              />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Hilton Auto Spare Parts
          </h1>
          <p className="text-lg text-gray-600">
            Management System - Choose Your Login Portal
          </p>
        </div>

        {/* Three Login Forms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* Admin Login Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 hover:shadow-3xl transition-shadow duration-300">
            <div className="px-6 pt-8 pb-6 border-b-2 border-indigo-600 bg-gradient-to-br from-indigo-50 to-indigo-100">
              <div className="flex flex-col items-center">
                <div className="relative mb-4">
                  <div className="h-16 w-16 rounded-full bg-indigo-600 flex items-center justify-center shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Administrator
                </h2>
                <p className="mt-1 text-xs text-gray-600 font-medium">
                  Full System Access
                </p>
              </div>
            </div>

            <div className="px-6 py-6">
              <form className="space-y-5" onSubmit={handleAdminSubmit}>
                
                <div>
                  <Input
                    label="Username"
                    type="text"
                    value={adminUsername}
                    onChange={(e) => setAdminUsername(e.target.value)}
                    required
                    autoComplete="username"
                    placeholder="admin_username"
                    className="transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <Input
                    label="Password"
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter password"
                    className="transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  isLoading={adminLoading}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {adminLoading ? 'Signing in...' : 'Sign in as Admin'}
                </Button>
              </form>
            </div>
          </div>

          {/* Client Login Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 hover:shadow-3xl transition-shadow duration-300">
            <div className="px-6 pt-8 pb-6 border-b-2 border-blue-600 bg-gradient-to-br from-blue-50 to-blue-100">
              <div className="flex flex-col items-center">
                <div className="relative mb-4">
                  <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Client
                </h2>
                <p className="mt-1 text-xs text-gray-600 font-medium">
                  Business Dashboard
                </p>
              </div>
            </div>

            <div className="px-6 py-6">
              <form className="space-y-5" onSubmit={handleClientSubmit}>
                
                <div>
                  <Input
                    label="Username"
                    type="text"
                    value={clientUsername}
                    onChange={(e) => setClientUsername(e.target.value)}
                    required
                    autoComplete="username"
                    placeholder="client_username"
                    className="transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <Input
                    label="Password"
                    type="password"
                    value={clientPassword}
                    onChange={(e) => setClientPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter password"
                    className="transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  isLoading={clientLoading}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {clientLoading ? 'Signing in...' : 'Sign in as Client'}
                </Button>
              </form>
            </div>
          </div>

          {/* Moderator Login Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 hover:shadow-3xl transition-shadow duration-300">
            <div className="px-6 pt-8 pb-6 border-b-2 border-emerald-600 bg-gradient-to-br from-emerald-50 to-emerald-100">
              <div className="flex flex-col items-center">
                <div className="relative mb-4">
                  <div className="h-16 w-16 rounded-full bg-emerald-600 flex items-center justify-center shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Moderator
                </h2>
                <p className="mt-1 text-xs text-gray-600 font-medium">
                  Client Operations
                </p>
              </div>
            </div>

            <div className="px-6 py-6">
              <form className="space-y-5" onSubmit={handleModeratorSubmit}>
                
                <div>
                  <Input
                    label="Username"
                    type="text"
                    value={moderatorUsername}
                    onChange={(e) => setModeratorUsername(e.target.value)}
                    required
                    autoComplete="username"
                    placeholder="moderator_username"
                    className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <Input
                    label="Password"
                    type="password"
                    value={moderatorPassword}
                    onChange={(e) => setModeratorPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter password"
                    className="transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  isLoading={moderatorLoading}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {moderatorLoading ? 'Signing in...' : 'Sign in as Moderator'}
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Select the appropriate login portal based on your role and access level
          </p>
        </div>
      </div>
    </div>
  );
};

